import type { ConfigType, Dayjs } from 'dayjs'
import type { z } from 'zod/v4'

import dayjs from '@/lib/dayjs'
import { FulfillmentType } from '@/lib/enums/FulfillmentType'
import { SalesOrderFulfillmentStatus } from '@/lib/enums/SalesOrderFulfillmentStatus'
import { SalesOrderPaymentStatus } from '@/lib/enums/SalesOrderPaymentStatus'
import { SalesOrderRefundStatus } from '@/lib/enums/SalesOrderRefundStatus'
import type { Address } from '@/lib/services/addresses/addresses.types'
import type { deliveryRoutesSearchSchema } from '@/lib/services/delivery-shares/delivery-shares.schema'
import type {
  DeliveryRouteStop,
  DeliveryRouteStopSource,
  DeliveryShare,
  DeliveryShareProgress,
} from '@/lib/services/delivery-shares/delivery-shares.types'
import type { LocationListItem } from '@/lib/services/locations/locations.types'
import { getLocationName } from '@/lib/services/locations/locations.utils'
import type {
  SalesOrderActiveDeliveryRoute,
  SalesOrderListItem,
} from '@/lib/services/sales-orders/sales-orders.types'
import { getErrorMessage, isRecord, readProperty } from '@/lib/utils'

import { DELIVERY_ROUTE_STOP_LIMIT } from './delivery-route-constants'
import { currentCustomerAllowsFulfillmentBeforePayment } from './sales-order-presentation'

export const DELIVERY_ROUTE_RECENTLY_COMPLETED_DAYS = 3

/** A sales order id, or a manual stop's `manual:<route stop id>` / `manual:new:<n>` key. */
export type DeliveryRouteSelectionId = number | string

type DeliveryRouteLocation = Pick<LocationListItem, 'id' | 'address'>

/**
 * A delivery the route pages work with: a sales order (list row or full order), a route stop
 * (`DeliveryRouteStop`), or a stop shown in an order's place. Each payload sends only some of these
 * fields: stops have no payment or route-membership fields, manual stops no order id, fulfillment
 * type, or status, and sales orders no stop source or route progress.
 */
export interface DeliveryRouteOrder {
  /** The sales order id; a stop shown in an order's place carries its `sales_order_id`. */
  id?: number | null
  selection_id?: DeliveryRouteSelectionId | null
  source_type?: DeliveryRouteStopSource
  organization_id?: number | null
  location?: DeliveryRouteLocation | null
  /** Manual stops shown in an order's place copy their route's location id. */
  location_id?: number | null
  delivery_address?: Pick<Address, 'line1' | 'city' | 'state'> | null
  delivery_destination_name?: string | null
  recipient_name?: string | null
  fulfillment_type?: SalesOrderListItem['fulfillment_type']
  fulfillment_status?: SalesOrderListItem['fulfillment_status']
  /** `YYYY-MM-DD` */
  fulfillment_due_date?: string | null
  payment_status?: SalesOrderListItem['payment_status']
  refund_status?: SalesOrderListItem['refund_status']
  cancelled_at?: string | null
  current_customer_allows_fulfillment_before_payment?: boolean
  allow_fulfillment_before_payment?: boolean
  delivery_share_status?: SalesOrderListItem['delivery_share_status']
  is_route_started?: boolean
  is_delivered?: boolean
}

/** A route's dates: a `DeliveryShare`, or a route built from stops the page is editing. */
interface DeliveryRouteDates {
  delivery_date?: string | null
  stops?: readonly Pick<DeliveryRouteOrder, 'fulfillment_due_date'>[]
}

/** A `DeliveryShare`, or an order's active route summary, which sends no dates or stops. */
type DeliveryRouteMenuRoute = Pick<SalesOrderActiveDeliveryRoute, 'name' | 'stop_count'> &
  DeliveryRouteDates

/** The route progress `isDeliveryRouteCompleted` reads (`DeliveryShareProgress`). */
type DeliveryRouteCompletion = Pick<
  DeliveryShareProgress,
  'is_completed' | 'status' | 'stop_count' | 'delivered_count' | 'missed_count' | 'remaining_count'
>

type DeliveryRouteIndexShare = DeliveryRouteCompletion &
  Pick<DeliveryShare, 'delivery_date' | 'completed_at' | 'created_at'>

/** The delivery routes page search (`deliveryRoutesSearchSchema`). */
export type DeliveryRoutesSearch = z.infer<typeof deliveryRoutesSearchSchema>

export interface DeliveryRouteIndexSections<Share> {
  pastDue: Share[]
  today: Share[]
  upcoming: Share[]
  undated: Share[]
  recentlyCompleted: Share[]
}

export interface DeliveryRouteCreateSections<Order> {
  pastDue: Order[]
  today: Order[]
  upcoming: Order[]
}

export function getDeliveryRouteSelectionId(
  order: Pick<DeliveryRouteOrder, 'selection_id' | 'id'> | null | undefined
): DeliveryRouteSelectionId | null | undefined {
  return order?.selection_id ?? order?.id
}

export function getDeliveryRouteServerStops<Stop>(
  route: { stops?: readonly Stop[] } | null | undefined
): readonly Stop[] {
  return route?.stops ?? []
}

export function getDeliveryRouteStopCount(
  route: { stop_count?: number; stops?: readonly unknown[] } | null | undefined
): number {
  const stopCount = Number(route?.stop_count ?? getDeliveryRouteServerStops(route).length)

  return Number.isFinite(stopCount) ? stopCount : 0
}

export function hasDeliveryRouteCapacity(
  route: { stop_count?: number; stops?: readonly unknown[] } | null | undefined
): boolean {
  return getDeliveryRouteStopCount(route) < DELIVERY_ROUTE_STOP_LIMIT
}

export function getDeliveryRouteSalesOrderIds(
  route:
    | { stops?: readonly Pick<DeliveryRouteStop, 'source_type' | 'sales_order_id'>[] }
    | null
    | undefined
): number[] {
  return getDeliveryRouteServerStops(route)
    .filter((stop) => stop.source_type === 'sales_order')
    .map((stop) => Number(stop.sales_order_id))
    .filter((id) => Number.isFinite(id))
}

export function getSingleActiveDeliveryRoute(
  order:
    | { delivery_share_status?: { active_routes?: SalesOrderActiveDeliveryRoute[] } }
    | null
    | undefined
): SalesOrderActiveDeliveryRoute | null {
  const activeRoutes = Array.isArray(order?.delivery_share_status?.active_routes)
    ? order.delivery_share_status.active_routes
    : []

  return activeRoutes.length === 1 ? (activeRoutes[0] ?? null) : null
}

export function getDeliveryRouteMenuLabels(route: DeliveryRouteMenuRoute | null | undefined): {
  primary: string
  secondary: string
} {
  const routeName = String(route?.name ?? '').trim()
  const dateSummary = getDeliveryRouteDateSummary(route)
  const stopCount = getDeliveryRouteStopCount(route)
  const stopLabel = stopCount === 1 ? '1 stop' : `${stopCount} stops`
  const primary = routeName || dateSummary
  const secondary =
    routeName && dateSummary && routeName.toLowerCase() !== dateSummary.toLowerCase()
      ? `${dateSummary} · ${stopLabel}`
      : stopLabel

  return { primary, secondary }
}

export function getDeliveryShareLocationId(
  order: Pick<DeliveryRouteOrder, 'location' | 'location_id'> | null | undefined
): number | null {
  return order?.location?.id ?? order?.location_id ?? null
}

export function canAddOrderToDeliveryRoute(
  route:
    | Pick<DeliveryShare, 'is_completed' | 'status' | 'location' | 'stop_count' | 'stops'>
    | null
    | undefined,
  order: DeliveryRouteOrder | null | undefined
): boolean {
  if (!route || !order || route.is_completed || route.status === 'completed') {
    return false
  }

  const orderLocationId = getDeliveryShareLocationId(order)
  const routeLocationId = route.location?.id ?? getDeliveryShareLocationId(route.stops[0])
  const routeSalesOrderIds = getDeliveryRouteSalesOrderIds(route)

  return (
    Boolean(orderLocationId) &&
    orderLocationId === routeLocationId &&
    !routeSalesOrderIds.includes(Number(order.id)) &&
    hasDeliveryRouteCapacity(route)
  )
}

export function getDeliveryShareStatus(order: DeliveryRouteOrder | null | undefined): {
  activeRouteCount: number
  isInActiveRoute: boolean
} {
  const activeRouteCount = Number(order?.delivery_share_status?.active_route_count || 0)

  return {
    activeRouteCount,
    isInActiveRoute:
      Boolean(order?.delivery_share_status?.is_in_active_route) || activeRouteCount > 0,
  }
}

export function isDeliveryShareEligible(order: DeliveryRouteOrder | null | undefined): boolean {
  if (order?.source_type === 'manual') {
    return Boolean(order?.delivery_address)
  }

  const paymentAllowsFulfillment =
    order?.payment_status === SalesOrderPaymentStatus.Paid.Value ||
    currentCustomerAllowsFulfillmentBeforePayment(order)

  return (
    order?.fulfillment_type === FulfillmentType.Delivery.Value &&
    !order?.cancelled_at &&
    paymentAllowsFulfillment &&
    order?.fulfillment_status !== SalesOrderFulfillmentStatus.Fulfilled.Value &&
    order?.refund_status !== SalesOrderRefundStatus.Refunded.Value &&
    Boolean(order?.delivery_address)
  )
}

export function isDeliveryRouteEligible(order: DeliveryRouteOrder | null | undefined): boolean {
  return (
    isDeliveryShareEligible(order) &&
    Boolean(order?.fulfillment_due_date) &&
    !getDeliveryShareStatus(order).isInActiveRoute
  )
}

export function getDeliveryRouteAction(
  order: DeliveryRouteOrder | null | undefined,
  { isReadOnly = false }: { isReadOnly?: boolean } = {}
): 'view' | 'create' | null {
  const deliveryShareStatus = getDeliveryShareStatus(order)

  if (deliveryShareStatus.activeRouteCount > 0) {
    return 'view'
  }

  if (isDeliveryRouteEligible(order) && !isReadOnly) {
    return 'create'
  }

  return null
}

export function getDeliveryRouteSelectionAnchor<Order extends { id: number }>(
  routeableOrders: readonly Order[],
  selectedOrderIds: readonly DeliveryRouteSelectionId[] | null | undefined
): Order | null {
  if (!selectedOrderIds?.length) {
    return null
  }

  const selectedOrderIdSet = new Set<DeliveryRouteSelectionId>(selectedOrderIds)

  return routeableOrders.find((order) => selectedOrderIdSet.has(order.id)) ?? null
}

export function canBundleDeliveryShareOrders(
  anchorOrder: DeliveryRouteOrder | null | undefined,
  candidateOrder: DeliveryRouteOrder | null | undefined
): boolean {
  if (!anchorOrder || !candidateOrder) {
    return false
  }

  const anchorOrganizationId = anchorOrder.organization_id
  const candidateOrganizationId = candidateOrder.organization_id
  const hasOrganizationMismatch =
    anchorOrganizationId !== null &&
    anchorOrganizationId !== undefined &&
    candidateOrganizationId !== null &&
    candidateOrganizationId !== undefined &&
    anchorOrganizationId !== candidateOrganizationId

  if (hasOrganizationMismatch) {
    return false
  }

  return getDeliveryShareLocationId(anchorOrder) === getDeliveryShareLocationId(candidateOrder)
}

export function getDeliveryRouteLocationMismatchMessage(
  order: DeliveryRouteOrder | null | undefined,
  anchorOrder: DeliveryRouteOrder | null | undefined
): string | null {
  if (!anchorOrder || !order || canBundleDeliveryShareOrders(anchorOrder, order)) {
    return null
  }

  const routeLocation = getDeliveryRouteLocationLabel(anchorOrder.location)
  const orderLocation = getDeliveryRouteLocationLabel(order.location)

  if (routeLocation === orderLocation) {
    return 'Routes can only include deliveries from one shop location.'
  }

  return `Routes can only include deliveries from ${routeLocation}. This delivery is from ${orderLocation}.`
}

export function isDeliveryShareSelectable(
  order: DeliveryRouteOrder | null | undefined,
  anchorOrder: DeliveryRouteOrder | null | undefined
): boolean {
  if (!isDeliveryRouteEligible(order)) {
    return false
  }

  if (
    !anchorOrder ||
    getDeliveryRouteSelectionId(anchorOrder) === getDeliveryRouteSelectionId(order)
  ) {
    return true
  }

  return canBundleDeliveryShareOrders(anchorOrder, order)
}

export function getDeliveryRouteBadgeLabel(
  order: DeliveryRouteOrder | null | undefined
): string | null {
  const { activeRouteCount } = getDeliveryShareStatus(order)

  if (activeRouteCount <= 0) {
    return null
  }

  return activeRouteCount === 1 ? 'In 1 active route' : `In ${activeRouteCount} active routes`
}

export function getDeliveryRouteOrderLabel(order: DeliveryRouteOrder | null | undefined): string {
  if (order?.source_type === 'manual') {
    const recipientName = String(order?.recipient_name ?? '').trim()

    return recipientName ? `Custom stop — ${recipientName}` : 'Custom stop'
  }

  return `Order #${order?.id}`
}

export function getDeliveryRoutesSearch(
  order: DeliveryRouteOrder | null | undefined,
  overrides: DeliveryRoutesSearch = {}
): DeliveryRoutesSearch {
  const locationId = getDeliveryShareLocationId(order)

  return {
    ...(order?.id ? { salesOrderId: order.id } : {}),
    ...(locationId ? { locationId } : {}),
    ...overrides,
  }
}

export function getSafeDayjs(value: ConfigType): Dayjs | null {
  if (value === null || value === undefined || value === '') {
    return null
  }

  try {
    const parsedDate = dayjs(value)

    return parsedDate.isValid() ? parsedDate : null
  } catch {
    return null
  }
}

export function formatDeliveryRouteDate(date: ConfigType): string {
  const parsedDate = getSafeDayjs(date)

  if (!parsedDate) {
    return 'No delivery date'
  }

  return parsedDate.format('MMM D, YYYY')
}

export function formatDeliveryRouteDateTime(date: ConfigType): string | null {
  const parsedDate = getSafeDayjs(date)

  if (!parsedDate) {
    return null
  }

  return parsedDate.format('MMM D, YYYY h:mm A')
}

export function getDeliveryRouteDateSummary(route: DeliveryRouteDates | null | undefined): string {
  const dates = getDeliveryRouteServerStops(route)
    .reduce<string[]>((values, salesOrder) => {
      const parsedDate = getSafeDayjs(salesOrder.fulfillment_due_date)

      if (!parsedDate) {
        return values
      }

      values.push(parsedDate.format('YYYY-MM-DD'))
      return values
    }, [])
    .sort()

  if (dates.length === 0) {
    return formatDeliveryRouteDate(route?.delivery_date)
  }

  if (dates[0] === dates[dates.length - 1]) {
    return formatDeliveryRouteDate(dates[0])
  }

  return `${formatDeliveryRouteDate(dates[0])} - ${formatDeliveryRouteDate(dates[dates.length - 1])}`
}

export function getDeliveryRouteLocationLabel(
  location: DeliveryRouteLocation | null | undefined
): string {
  return location ? getLocationName(location) : 'Unknown location'
}

export function getDeliveryRouteLocationAddress(
  location: Pick<LocationListItem, 'address'> | null | undefined
): string {
  const cityLine = [location?.address?.city, location?.address?.state].filter(Boolean).join(', ')

  return (
    [
      location?.address?.line1,
      cityLine,
      location?.address?.postal_code,
      location?.address?.country && location.address.country !== 'US'
        ? location.address.country
        : null,
    ]
      .filter(Boolean)
      .join(' · ') || 'No address on file'
  )
}

export function getDeliveryRouteOrderAddress(order: DeliveryRouteOrder | null | undefined): string {
  const streetAddress = [
    order?.delivery_address?.line1,
    order?.delivery_address?.city,
    order?.delivery_address?.state,
  ]
    .filter(Boolean)
    .join(', ')

  return (
    [order?.delivery_destination_name, streetAddress].filter(Boolean).join(' · ') ||
    'Missing delivery address'
  )
}

function getComparableTimestamp(value: ConfigType, fallback: number): number {
  const timestamp = getSafeDayjs(value)?.valueOf()

  return timestamp !== undefined && Number.isFinite(timestamp) ? timestamp : fallback
}

type CreatedAtFields = { created_at?: string | null }
type DeliveryDateFields = { delivery_date?: string | null }

function compareCreatedAtAsc(left: CreatedAtFields, right: CreatedAtFields): number {
  return (
    getComparableTimestamp(left?.created_at, Number.MAX_SAFE_INTEGER) -
    getComparableTimestamp(right?.created_at, Number.MAX_SAFE_INTEGER)
  )
}

function compareCreatedAtDesc(left: CreatedAtFields, right: CreatedAtFields): number {
  return (
    getComparableTimestamp(right?.created_at, Number.MIN_SAFE_INTEGER) -
    getComparableTimestamp(left?.created_at, Number.MIN_SAFE_INTEGER)
  )
}

function compareDeliveryDateAsc(left: DeliveryDateFields, right: DeliveryDateFields): number {
  return (
    getComparableTimestamp(left?.delivery_date, Number.MAX_SAFE_INTEGER) -
    getComparableTimestamp(right?.delivery_date, Number.MAX_SAFE_INTEGER)
  )
}

function compareCompletedAtDesc(
  left: DeliveryDateFields & { completed_at?: string | null },
  right: DeliveryDateFields & { completed_at?: string | null }
): number {
  return (
    getComparableTimestamp(right?.completed_at || right?.delivery_date, Number.MIN_SAFE_INTEGER) -
    getComparableTimestamp(left?.completed_at || left?.delivery_date, Number.MIN_SAFE_INTEGER)
  )
}

function compareFulfillmentDueDateAsc(
  left: Pick<DeliveryRouteOrder, 'fulfillment_due_date'>,
  right: Pick<DeliveryRouteOrder, 'fulfillment_due_date'>
): number {
  return (
    getComparableTimestamp(left?.fulfillment_due_date, Number.MAX_SAFE_INTEGER) -
    getComparableTimestamp(right?.fulfillment_due_date, Number.MAX_SAFE_INTEGER)
  )
}

export function isDeliveryRouteCompleted(
  share: DeliveryRouteCompletion | null | undefined
): boolean {
  if (share?.is_completed || share?.status === 'completed') {
    return true
  }

  const stopCount = Number(share?.stop_count || 0)
  const deliveredCount = Number(share?.delivered_count || 0)
  const missedCount = Number(share?.missed_count || 0)
  const visibleRemainingCount = Math.max(stopCount - deliveredCount - missedCount, 0)
  const serverRemainingCount = Number(share?.remaining_count)
  const remainingCount = Number.isFinite(serverRemainingCount)
    ? Math.max(visibleRemainingCount, serverRemainingCount)
    : visibleRemainingCount

  return stopCount > 0 && remainingCount === 0
}

export function isDeliveryRouteOrderOutForDelivery(
  order: DeliveryRouteOrder | null | undefined
): boolean {
  if (typeof order?.is_route_started === 'boolean') {
    return order.is_route_started
  }

  return order?.fulfillment_status === SalesOrderFulfillmentStatus.Out.Value
}

export function isDeliveryRouteStarted(
  share:
    | (DeliveryRouteCompletion &
        Pick<DeliveryShare, 'started_at'> & { stops: readonly DeliveryRouteOrder[] })
    | null
    | undefined
): boolean {
  if (isDeliveryRouteCompleted(share)) {
    return false
  }

  if (share?.started_at) {
    return true
  }

  return getDeliveryRouteServerStops(share).some(
    (order) => isDeliveryRouteOrderOutForDelivery(order) && !order.is_delivered
  )
}

export function getDeliveryRouteMissedCount(
  share: Pick<DeliveryShareProgress, 'missed_count'> | null | undefined
): number {
  return Number(share?.missed_count || 0)
}

export function buildDeliveryRouteIndexSections<Share extends DeliveryRouteIndexShare>(
  shares: readonly Share[],
  {
    today = dayjs().format('YYYY-MM-DD'),
    recentlyCompletedDays = DELIVERY_ROUTE_RECENTLY_COMPLETED_DAYS,
  }: { today?: string; recentlyCompletedDays?: number } = {}
): DeliveryRouteIndexSections<Share> {
  const todayDate = getSafeDayjs(today) ?? dayjs()
  const recentlyCompletedCutoff = todayDate.subtract(Math.max(recentlyCompletedDays - 1, 0), 'day')

  const sections: DeliveryRouteIndexSections<Share> = {
    pastDue: [],
    today: [],
    upcoming: [],
    undated: [],
    recentlyCompleted: [],
  }

  shares.forEach((share) => {
    const deliveryDate = getSafeDayjs(share.delivery_date)
    const completedAt = getSafeDayjs(share.completed_at) ?? deliveryDate

    if (isDeliveryRouteCompleted(share)) {
      if (
        completedAt &&
        !completedAt.isBefore(recentlyCompletedCutoff, 'day') &&
        !completedAt.isAfter(todayDate, 'day')
      ) {
        sections.recentlyCompleted.push(share)
      }

      return
    }

    if (!deliveryDate) {
      if (!share.delivery_date) {
        sections.undated.push(share)
      }

      return
    }

    if (deliveryDate.isBefore(todayDate, 'day')) {
      sections.pastDue.push(share)
      return
    }

    if (deliveryDate.isSame(todayDate, 'day')) {
      sections.today.push(share)
      return
    }

    sections.upcoming.push(share)
  })

  sections.pastDue.sort(
    (left, right) => compareDeliveryDateAsc(left, right) || compareCreatedAtAsc(left, right)
  )
  sections.today.sort(compareCreatedAtAsc)
  sections.upcoming.sort(
    (left, right) => compareDeliveryDateAsc(left, right) || compareCreatedAtAsc(left, right)
  )
  sections.undated.sort(compareCreatedAtAsc)
  sections.recentlyCompleted.sort(
    (left, right) => compareCompletedAtDesc(left, right) || compareCreatedAtDesc(left, right)
  )

  return sections
}

export function filterDeliveryRoutesByDate<Share extends DeliveryRouteIndexShare>(
  shares: readonly Share[],
  due: string
): Share[] {
  return shares
    .filter((share) => share.delivery_date === due)
    .sort((left, right) => {
      const leftCompleted = isDeliveryRouteCompleted(left)
      const rightCompleted = isDeliveryRouteCompleted(right)

      if (leftCompleted !== rightCompleted) {
        return leftCompleted ? 1 : -1
      }

      return compareCreatedAtAsc(left, right)
    })
}

export function buildDeliveryRouteCreateSections<Order extends DeliveryRouteOrder>(
  orders: readonly Order[],
  { today = dayjs().format('YYYY-MM-DD') }: { today?: string } = {}
): DeliveryRouteCreateSections<Order> {
  const todayDate = getSafeDayjs(today) ?? dayjs()
  const sections: DeliveryRouteCreateSections<Order> = {
    pastDue: [],
    today: [],
    upcoming: [],
  }

  orders.forEach((order) => {
    if (!isDeliveryRouteEligible(order)) {
      return
    }

    const deliveryDate = getSafeDayjs(order.fulfillment_due_date)

    if (!deliveryDate) {
      return
    }

    if (deliveryDate.isBefore(todayDate, 'day')) {
      sections.pastDue.push(order)
      return
    }

    if (deliveryDate.isSame(todayDate, 'day')) {
      sections.today.push(order)
      return
    }

    sections.upcoming.push(order)
  })

  const sortOrders = (left: Order, right: Order) =>
    compareFulfillmentDueDateAsc(left, right) ||
    getDeliveryRouteLocationLabel(left.location).localeCompare(
      getDeliveryRouteLocationLabel(right.location)
    ) ||
    Number(left.id || 0) - Number(right.id || 0)

  sections.pastDue.sort(sortOrders)
  sections.today.sort(sortOrders)
  sections.upcoming.sort(sortOrders)

  return sections
}

// The first message in a field's validation errors, skipping blank ones.
function findValidationMessage(messages: unknown): string | undefined {
  if (!Array.isArray(messages)) {
    return undefined
  }

  const candidates: unknown[] = messages

  return candidates.find(
    (candidate): candidate is string => typeof candidate === 'string' && candidate.trim() !== ''
  )
}

export function getDeliveryShareErrorMessage(error: unknown): string | undefined {
  const validationErrors = readProperty(
    readProperty(readProperty(error, 'response'), 'data'),
    'errors'
  )
  const preferredFields = ['stops', 'location_id', 'sales_order_ids', 'destination', 'channel']

  for (const field of preferredFields) {
    const message = findValidationMessage(readProperty(validationErrors, field))

    if (message) {
      return message
    }
  }

  const nestedStopMessage = Object.entries(isRecord(validationErrors) ? validationErrors : {})
    .filter(([field]) => field.startsWith('stops.'))
    .map(([, messages]) => findValidationMessage(messages))
    .find(Boolean)

  return nestedStopMessage || getErrorMessage(error)
}
